import { useEffect, useState } from 'react'
import AIDocumentAssistant from '../components/AIDocumentAssistant'
import { supabase } from '../lib/supabase'
import { useAuth } from '../context/AuthContext'

const ENTITY_LABELS = { '1040': '1040 — Personal', '1120S': '1120S — S-Corp', '1065': '1065 — Partnership', '1120': '1120 — C-Corp', 'schedule_c': 'Schedule C — Sole Prop', '990': '990 — Nonprofit' }

const DEFAULT_SERVICES = {
  '1040':       [{ name: 'Federal Individual Income Tax Return (Form 1040)', fee: '' }, { name: 'State Income Tax Return', fee: '' }, { name: 'Tax Planning Consultation', fee: '' }],
  '1120S':      [{ name: 'S-Corporation Federal Tax Return (Form 1120S)', fee: '' }, { name: 'State Corporate Tax Return', fee: '' }, { name: 'K-1 Preparation', fee: '' }, { name: 'Payroll Tax Review', fee: '' }],
  '1065':       [{ name: 'Partnership Federal Tax Return (Form 1065)', fee: '' }, { name: 'State Partnership Return', fee: '' }, { name: 'K-1 Preparation (per partner)', fee: '' }],
  '1120':       [{ name: 'C-Corporation Federal Tax Return (Form 1120)', fee: '' }, { name: 'State Corporate Tax Return', fee: '' }],
  'schedule_c': [{ name: 'Schedule C — Sole Proprietorship', fee: '' }, { name: 'Federal Individual Income Tax Return (Form 1040)', fee: '' }, { name: 'State Income Tax Return', fee: '' }],
  '990':        [{ name: 'Nonprofit Tax Return (Form 990)', fee: '' }, { name: 'State Nonprofit Filing', fee: '' }],
}

export default function EngagementLetterPage() {
  const { profile } = useAuth()
  const [clients, setClients] = useState([])
  const [selectedClient, setSelectedClient] = useState('')
  const [clientData, setClientData] = useState(null)
  const [services, setServices] = useState([{ name: '', fee: '' }])
  const [feeTotal, setFeeTotal] = useState('')
  const [taxYear, setTaxYear] = useState(new Date().getFullYear())
  const [notes, setNotes] = useState('')
  const [generating, setGenerating] = useState(false)
  const [showAI, setShowAI] = useState(false)
  const [generated, setGenerated] = useState(null)
  const [error, setError] = useState('')

  useEffect(() => {
    supabase.from('clients').select('id, full_name, business_name, entity_type, tax_year, email')
      .eq('cpa_id', profile.id).order('business_name').then(({ data }) => setClients(data || []))
  }, [])

  function handleClientChange(id) {
    setSelectedClient(id)
    const client = clients.find(c => c.id === id)
    setClientData(client || null)
    if (client) {
      setServices(DEFAULT_SERVICES[client.entity_type] || [{ name: '', fee: '' }])
      setTaxYear(client.tax_year || new Date().getFullYear())
    }
    setGenerated(null)
    setError('')
  }

  function updateService(i, field, value) {
    setServices(prev => prev.map((s, idx) => idx === i ? { ...s, [field]: value } : s))
  }

  function addService() {
    setServices(prev => [...prev, { name: '', fee: '' }])
  }

  function removeService(i) {
    setServices(prev => prev.filter((_, idx) => idx !== i))
  }

  const calculatedTotal = services.reduce((sum, s) => sum + (parseFloat(s.fee) || 0), 0)

  function applyAIData(data) {
    if (data.clientName && !selectedClient) {
      // Try to find matching client
      const match = clients.find(c =>
        (c.business_name || c.full_name)?.toLowerCase().includes(data.clientName.toLowerCase())
      )
      if (match) setSelectedClient(match.id)
    }
    if (data.taxYear) setTaxYear(data.taxYear)
    if (data.services?.length) {
      setServices(data.services.map(s => ({
        name: s.name || '',
        fee: s.fee || ''
      })))
    }
    if (data.notes) setNotes(data.notes)
    if (data.feeTotal) setFeeTotal(data.feeTotal)
  }

  async function handleGenerate() {
    if (!selectedClient) { setError('Please select a client'); return }
    setGenerating(true); setError(''); setGenerated(null)

    try {
      const { data: { session } } = await supabase.auth.getSession()
      const res = await fetch('/.netlify/functions/generate-engagement-letter', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session?.access_token}`
        },
        body: JSON.stringify({
          client_id: selectedClient,
          services: services.filter(s => s.name),
          fee_total: feeTotal || calculatedTotal || null,
          tax_year: taxYear,
          notes
        })
      })
      const data = await res.json()
      if (data.error) { setError(data.error); setGenerating(false); return }
      setGenerated(data)
    } catch (err) {
      setError(err.message)
    }
    setGenerating(false)
  }

  function downloadPDF() {
    if (!generated?.html) return
    const html = atob(generated.html)
    const blob = new Blob([html], { type: 'text/html' })
    const url = URL.createObjectURL(blob)
    const printWindow = window.open(url, '_blank')
    if (!printWindow) {
      // Popup blocked - fallback to direct write
      const w = window.open('', '_blank')
      if (w) { w.document.write(html); w.document.close(); w.focus() }
    }
  }

  return (
    <div style={{ maxWidth: 760 }}>
      {/* Header */}
      <div className="card" style={{ padding: '24px 28px', marginBottom: 24, background: '#202326', border: 'none' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h2 style={{ color: '#fff', fontSize: 20, marginBottom: 6 }}>Engagement Letter Generator</h2>
            <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: 13, margin: 0 }}>
              Generate a professional engagement letter pre-filled with client information.
            </p>
          </div>
          <button onClick={() => setShowAI(true)} style={{ background: 'rgba(201,168,76,0.15)', border: '1px solid rgba(201,168,76,0.3)', color: '#CBB17B', padding: '10px 18px', borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
            🤖 Generate with AI
          </button>
        </div>
      </div>

      {showAI && (
        <AIDocumentAssistant
          type="engagement"
          clients={clients}
          onApply={applyAIData}
          onClose={() => setShowAI(false)}
        />
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
        {/* Left — Setup */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {/* Client selection */}
          <div className="card" style={{ padding: '20px 22px' }}>
            <h3 style={{ fontSize: 14, fontWeight: 700, marginBottom: 14 }}>Client & Tax Year</h3>
            <div style={{ marginBottom: 12 }}>
              <label className="label">Select Client *</label>
              <select className="input" value={selectedClient} onChange={e => handleClientChange(e.target.value)}>
                <option value="">Choose a client…</option>
                {clients.map(c => <option key={c.id} value={c.id}>{c.business_name || c.full_name}</option>)}
              </select>
            </div>
            {clientData && (
              <div style={{ background: '#F7F3EB', borderRadius: 8, padding: '10px 14px', fontSize: 12, color: '#62615D' }}>
                <div><strong style={{ color: '#202326' }}>{clientData.business_name || clientData.full_name}</strong></div>
                <div>{ENTITY_LABELS[clientData.entity_type]}</div>
                <div>{clientData.email}</div>
              </div>
            )}
            <div style={{ marginTop: 12 }}>
              <label className="label">Tax Year</label>
              <input className="input" type="number" value={taxYear} onChange={e => setTaxYear(parseInt(e.target.value))} min="2020" max="2030" />
            </div>
          </div>

          {/* Additional notes */}
          <div className="card" style={{ padding: '20px 22px' }}>
            <h3 style={{ fontSize: 14, fontWeight: 700, marginBottom: 14 }}>Additional Notes</h3>
            <textarea className="input" rows={4} placeholder="Any special terms, conditions, or notes to include…" value={notes} onChange={e => setNotes(e.target.value)} style={{ resize: 'vertical' }} />
          </div>
        </div>

        {/* Right — Services */}
        <div className="card" style={{ padding: '20px 22px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
            <h3 style={{ fontSize: 14, fontWeight: 700 }}>Services & Fees</h3>
            <button className="btn btn-ghost" style={{ fontSize: 11 }} onClick={addService}>+ Add Service</button>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 16 }}>
            {services.map((s, i) => (
              <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                <input className="input" placeholder="Service description" value={s.name} onChange={e => updateService(i, 'name', e.target.value)} style={{ flex: 1, fontSize: 12 }} />
                <input className="input" placeholder="Fee" value={s.fee} onChange={e => updateService(i, 'fee', e.target.value)} style={{ width: 80, fontSize: 12 }} />
                <button onClick={() => removeService(i)} style={{ background: 'none', border: 'none', color: '#F87171', cursor: 'pointer', fontSize: 16, padding: '8px 4px', flexShrink: 0 }}>×</button>
              </div>
            ))}
          </div>

          {/* Fee total */}
          <div style={{ borderTop: '2px solid #CBB17B', paddingTop: 12 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <span style={{ fontSize: 12, color: '#62615D' }}>Auto-calculated total:</span>
              <span style={{ fontSize: 14, fontWeight: 700, color: '#202326' }}>${calculatedTotal.toFixed(2)}</span>
            </div>
            <label className="label">Override Total Fee (optional)</label>
            <input className="input" placeholder={`$${calculatedTotal.toFixed(2)}`} value={feeTotal} onChange={e => setFeeTotal(e.target.value)} style={{ fontSize: 12 }} />
          </div>
        </div>
      </div>

      {/* Generate button */}
      <div style={{ marginTop: 20 }}>
        {error && <div style={{ background: '#FEE2E2', color: '#991B1B', padding: '10px 14px', borderRadius: 8, fontSize: 13, marginBottom: 14 }}>{error}</div>}

        {generated ? (
          <div style={{ background: '#D1FAE5', border: '1px solid #6EE7B7', borderRadius: 10, padding: '20px 24px', display: 'flex', alignItems: 'center', gap: 16 }}>
            <div style={{ fontSize: 32 }}>✅</div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: '#065F46', marginBottom: 4 }}>Engagement letter generated!</div>
              <div style={{ fontSize: 12, color: '#62615D' }}>Saved to client's documents automatically.</div>
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <button className="btn btn-primary" onClick={downloadPDF}>⬇️ Download PDF</button>
              <button className="btn btn-ghost" onClick={() => setGenerated(null)}>Generate New</button>
            </div>
          </div>
        ) : (
          <button className="btn btn-primary" onClick={handleGenerate} disabled={generating || !selectedClient}
            style={{ width: '100%', justifyContent: 'center', padding: '14px', fontSize: 15 }}>
            {generating ? (
              <><span style={{ width: 16, height: 16, border: '2px solid rgba(201,168,76,0.3)', borderTopColor: '#CBB17B', borderRadius: '50%', display: 'inline-block', animation: 'spin 0.7s linear infinite' }} /> Generating…</>
            ) : '📄 Generate Engagement Letter'}
          </button>
        )}
      </div>
    </div>
  )
}
